/**
 * One square, one quality, one crop - wherever the picture is taken. A phone and the television's
 * own webcam both land in `setAvatar`, so a photo from either has to come out the same shape and the
 * same size: stored avatars sit in localStorage beside the profile, and a phone's full-resolution
 * frame is orders of magnitude larger than anything that belongs there.
 */

export const AVATAR_SIZE = 256;
export const AVATAR_QUALITY = 0.8;

interface Options {
  /** A front-facing camera is shown mirrored, and a photo of yourself that flips when taken reads as wrong. */
  mirrored?: boolean;
}

/** The centre square of a running video, as a data URL - or `null` before the first frame has arrived. */
export function captureAvatar(video: HTMLVideoElement, { mirrored = true }: Options = {}): string | null {
  if (!video.videoWidth || !video.videoHeight) return null;

  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const context = canvas.getContext('2d');
  if (!context) return null;

  if (mirrored) {
    context.translate(AVATAR_SIZE, 0);
    context.scale(-1, 1);
  }

  // Centre-cropped to a square, so a 16:9 camera doesn't come out stretched. The avatar is drawn
  // inside a circle inscribed in this square, which is what the phone's crop guide draws.
  const side = Math.min(video.videoWidth, video.videoHeight);
  const left = (video.videoWidth - side) / 2;
  const top = (video.videoHeight - side) / 2;
  context.drawImage(video, left, top, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);

  return canvas.toDataURL('image/jpeg', AVATAR_QUALITY);
}
